import { gql, useQuery } from '@sortlist-frontend/data-fetching/public';
import { useState } from 'react';

import { acceptLanguageHeader } from '_core/api/api-nextjs-proxy';

export const AWARDS_PER_PAGE = 100;

export const AGENCY_AWARDS_QUERY = gql(`
  query GetAgencyAwards($slug: String!, $first: Int!, $after: String) {
    agency(slug: $slug) {
      id
      awards(first: $first, after: $after) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
        }
        nodes {
          id
          name
          category
          wonAt
          work {
            id
            slug
          }
        }
      }
    }
  }
`);

export const useGetAgencyAwards = ({
  agencySlug,
  locale,
  perPage = AWARDS_PER_PAGE,
}: {
  agencySlug: string;
  locale: string;
  perPage?: number;
}) => {
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const { data, fetchMore, loading, ...rest } = useQuery(AGENCY_AWARDS_QUERY, {
    variables: { slug: agencySlug, first: perPage },
    context: {
      headers: {
        'Accept-Language': acceptLanguageHeader(locale),
      },
    },
  });

  const awards = data?.agency?.awards;
  const hasNextPage = awards?.pageInfo.hasNextPage ?? false;

  const fetchMoreAwards = async () => {
    const endCursor = awards?.pageInfo.endCursor;
    if (endCursor == null || isFetchingMore) return;

    setIsFetchingMore(true);
    try {
      await fetchMore({
        variables: { after: endCursor },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (fetchMoreResult?.agency?.awards == null) return previousResult;

          return {
            ...previousResult,
            agency: {
              ...previousResult.agency,
              awards: {
                ...fetchMoreResult.agency.awards,
                nodes: [
                  ...(previousResult.agency?.awards?.nodes ?? []),
                  ...(fetchMoreResult.agency.awards.nodes ?? []),
                ],
              },
            },
          };
        },
      });
    } finally {
      setIsFetchingMore(false);
    }
  };

  return {
    awards,
    isLoading: loading,
    isFetchingMore,
    hasNextPage,
    fetchMoreAwards,
    ...rest,
  };
};
